import dayjs from 'dayjs';
import { MinusCircle, PlusCircle } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import _ from 'underscore';

import { useAccountState } from 'contexts/account/account-context';

import Mui from 'materials';

import { AxiosResponse } from 'axios';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import Collapsible from 'components/collapsible';
import DatePicker from 'components/date-picker';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';

import ActionButtons from 'components/action-buttons';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { useSiteSettingsState } from 'views/site-settings/context/site-settings-context';
import { useUserFeatures } from 'views/site-settings/hooks/use-user-features';
import { getSiteSettingsProductAssortment, putRecommendedProducts } from '../api/recommended-products';
import { UpdateRecommendedProductsInput } from '../types/update-recommended-products-input.type';
import { Grid, useMediaQuery, useTheme } from '@mui/material';

type ParamTypes = {
  globalSiteId: string;
};

type Props = {};

const PAGE_NAME_ID = 'siteSettings.editSiteSettings.recommendedProducts';

const RecommendedProducts: React.FC<Props> = ({}) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();
  const {
    siteSettingsQuery: { data: settings, isLoading },
  } = useSiteSettingsState();
  const { canEditSiteSettings } = useUserFeatures();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isLoading) return <LoadingSpinner />; // bad solution?
  if (!settings) return <></>;

  // #region STATE
  const [edit, setEdit] = useState(false);
  const [productSearchValue, setProductSearchValue] = useState<string>('');
  // #endregion STATE

  // #region FETCH DATA
  const { data: productAssortment, isLoading: isProductAssortmentLoading } = useGet(
    ['getSiteSettingsProductAssortment', params.globalSiteId],
    ({ queryKey }) => getSiteSettingsProductAssortment(String(queryKey[1])),
    {
      enabled: !!params.globalSiteId && !!account.company?.addressAccess.find((s) => s.id === params.globalSiteId)?.siteErpId,
      select: (data) => {
        return _.chain(data)
          .sortBy((product) => product.itemDescription.toLowerCase() ?? product.itemName.toLowerCase())
          .value();
      },
    }
  );
  // #endregion FETCH DATA

  // #region POST DATA
  const updateRecommendedProducts = usePost<AxiosResponse, UpdateRecommendedProductsInput>(
    (request) => putRecommendedProducts(params.globalSiteId, request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getSiteSettings', params.globalSiteId],
        exact: true
      });
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      setEdit(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
      false;
      setEdit(false);
    }
  );

  // #region FORM

  const defaultValues = useMemo(() => {
    if (!settings.recommendedProducts.length) {
      return { recommendedProducts: [{ productCode: '', expirationDate: '' }] };
    }

    return { recommendedProducts: settings.recommendedProducts };
  }, [settings.recommendedProducts]);

  const { handleSubmit, control, reset } = useForm<UpdateRecommendedProductsInput>({
    defaultValues: defaultValues,
    shouldUnregister: false,
  });

  const { fields, remove, prepend } = useFieldArray({
    control: control,
    name: 'recommendedProducts',
    shouldUnregister: true,
  });

  // #endregion FORM

  // #region HANDLERS
  const handleUpdateRecommendedProducts = (request: UpdateRecommendedProductsInput) => {
    updateRecommendedProducts.mutate(request);
  };

  const handleEdit = (edited: boolean) => setEdit(edited);

  const handleCancel = () => {
    reset();
    setEdit(false);
  };

  const watchRecommendedProducts = useWatch({
    control: control,
    name: 'recommendedProducts',
  });

  // #endregion HANDLERS
  return (
    <Collapsible label={intl.formatMessage({ id: `${PAGE_NAME_ID}.title` })}>
      <form onSubmit={handleSubmit((data) => handleUpdateRecommendedProducts(data))}>
        <Grid container spacing={6} pb={4} px={isMobile ? 4 : 0}>
          <Grid size={12}>
            <ActionButtons
              isDisabled={canEditSiteSettings}
              isEditing={edit}
              onEdit={handleEdit}
              onCancel={handleCancel}
              onSave={handleSubmit((data) => handleUpdateRecommendedProducts(data))}
            />
            {updateRecommendedProducts.isPending && <LoadingSpinner overlay />}
          </Grid>
          {fields.map((field, idx) => (
            <React.Fragment key={field.id}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Select
                  dark
                  fullWidth
                  MenuProps={{ autoFocus: false }} // disable to set focus to search text field
                  onClose={() => {
                    setProductSearchValue('');
                  }}
                  search={{
                    value: productSearchValue,
                    onChange: setProductSearchValue,
                  }}
                  isLoading={isProductAssortmentLoading}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.product.productName` })}
                  controller={{
                    name: `recommendedProducts.${idx}.productCode`,
                    control: control,
                    rules: {
                      required: true,
                    },
                  }}
                  disabled={!edit || isProductAssortmentLoading}>
                  {productAssortment
                    ?.filter((product) => product.itemDescription.toLowerCase().includes(productSearchValue))
                    .map((product, idx) => (
                      <RadioSelectItem
                        key={`${product.itemNumber}_${idx}`}
                        value={product.itemNumber}
                        selected={product.itemNumber === watchRecommendedProducts[idx]?.productCode}
                        disabled={watchRecommendedProducts.map((product) => product.productCode).includes(product.itemNumber)}
                        sx={{ flexDirection: 'column', alignItems: 'flex-start', height: 'auto' }}>
                        {product.itemDescription ?? product.itemName}
                        <small>
                          {product.type}, Packaging: {product.packaging}
                        </small>
                      </RadioSelectItem>
                    ))}
                </Select>
              </Grid>
              <Grid size={{ xs: 9, md: 4 }}>
                <DatePicker
                  dark
                  fullWidth
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.product.expirationDate` })}
                  controller={{
                    name: `recommendedProducts.${idx}.expirationDate`,
                    control: control,
                    rules: { required: true },
                  }}
                  disablePast={true}
                  disabled={!edit || isProductAssortmentLoading}
                />
              </Grid>
              <Grid size={{ xs: 3, md: 1 }} alignItems={'end'}>
                {idx === 0 ? (
                  <>
                    <Mui.IconButton
                      color="primary"
                      sx={{ mr: 2 }}
                      title="Add Recommended Product"
                      disabled={!edit || watchRecommendedProducts?.length > 2}
                      onClick={() =>
                        prepend({
                          productCode: '',
                          expirationDate: dayjs(_.first(watchRecommendedProducts)?.expirationDate).toString() ?? '',
                        })
                      }>
                      <PlusCircle size={46} strokeWidth={1} />
                    </Mui.IconButton>
                  </>
                ) : (
                  <Mui.IconButton
                    disabled={!edit}
                    color="primary"
                    sx={{ mr: 2 }}
                    title="Remove Recommended Product"
                    onClick={() => remove(idx)}>
                    <MinusCircle size={46} strokeWidth={1} />
                  </Mui.IconButton>
                )}
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </form>
    </Collapsible>
  );
};

export default React.memo(RecommendedProducts);
